import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import MellanModal from "./MellanModal";
import BookingBar from "./BookingBar";
import { FP_LEVELS, OTHER_SERVICES, SELECTION_KEY } from "../data/site";
import { trackEvent } from "../utils/analytics";

/** Small SVG identity per level: which glass surfaces are included. */
const LevelGlyph = ({ variant, active }) => {
  const glass = active ? "#0E62FE" : "#93C5FD";
  const off = "#DBEAFE";
  return (
    <svg viewBox="0 0 48 48" className="h-11 w-11" aria-hidden="true">
      <rect x="6" y="6" width="36" height="36" rx="6" fill={active ? "#EBF3FF" : "#F4F8FE"} stroke={active ? "#0E62FE" : "#CBD5E1"} strokeWidth="2" />
      {variant === "utsida" && <rect x="13" y="13" width="22" height="22" rx="3" fill={glass} fillOpacity="0.85" />}
      {variant === "in-och-utsida" && (
        <>
          <rect x="13" y="13" width="22" height="22" rx="3" fill={glass} fillOpacity="0.85" />
          <rect x="17" y="17" width="14" height="14" rx="2" fill={off} />
        </>
      )}
      {variant === "premium" && (
        <>
          <rect x="13" y="13" width="22" height="22" rx="3" fill={glass} fillOpacity="0.85" />
          <rect x="17" y="17" width="14" height="14" rx="2" fill={off} />
          <rect x="6" y="6" width="36" height="36" rx="6" fill="none" stroke={active ? "#0E62FE" : "#60A5FA"} strokeWidth="2.5" strokeDasharray="4 4" />
        </>
      )}
    </svg>
  );
};

export const Configurator = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [mellan, setMellan] = useState(false);
  const [asked, setAsked] = useState(() => {
    try { return !!JSON.parse(sessionStorage.getItem(SELECTION_KEY))?.asked; } catch { return false; }
  });
  const [showMellan, setShowMellan] = useState(false);

  const persist = (level, mellanVal, askedVal) => {
    try { sessionStorage.setItem(SELECTION_KEY, JSON.stringify({ level, mellan: mellanVal, asked: askedVal })); } catch { /* ignore */ }
  };

  const select = (lvl) => {
    setSelected(lvl);
    persist(lvl.name, mellan, asked);
    if (!asked) setShowMellan(true);
  };

  const addMellan = () => {
    setMellan(true);
    setAsked(true);
    setShowMellan(false);
    persist(selected?.name, true, true);
    toast.success("Mellan fönstren tillagt i din bokning.");
  };
  const declineMellan = () => {
    setAsked(true);
    setShowMellan(false);
    persist(selected?.name, mellan, true);
  };
  const removeMellan = () => {
    setMellan(false);
    persist(selected?.name, false, true);
  };

  const goBooking = () => {
    persist(selected?.name, mellan, true);
    trackEvent("boka_click", { location: "configurator", level: selected?.name, mellan_fonstren: mellan });
    navigate("/boka-offert");
  };

  return (
    <section id="tjanster" className="bg-white scroll-mt-24" data-testid="configurator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue">Våra tjänster</p>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-ink leading-tight">
              Välj din fönsterputs – <span className="font-accent italic font-semibold text-brand-blue">vi sköter resten.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Tre tydliga nivåer. Välj den som passar dig – och lägg till Mellan fönstren om du har kopplade fönster.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEVEL SELECTOR */}
          <div className="lg:col-span-7 space-y-4" role="radiogroup" aria-label="Välj nivå av fönsterputs">
            {FP_LEVELS.map((lvl, i) => {
              const on = selected?.id === lvl.id;
              return (
                <Reveal key={lvl.id} delay={i * 0.07}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => select(lvl)}
                    data-testid={`service-card-${lvl.id}`}
                    className={`group w-full text-left rounded-2xl p-5 md:p-6 flex items-center gap-5 border-2 transition-all duration-300 ${
                      on
                        ? "bg-brand-iceBg border-brand-blue shadow-[0_20px_40px_-15px_rgba(14,98,254,0.15)]"
                        : "bg-white border-slate-100 shadow-[0_2px_10px_rgba(7,22,44,0.04)] hover:border-brand-blue/50 hover:-translate-y-0.5"
                    }`}
                  >
                    <LevelGlyph variant={lvl.id} active={on} />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2.5">
                        <span className="font-heading text-lg md:text-xl font-bold text-brand-ink">{lvl.name}</span>
                        {lvl.badge && (
                          <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md bg-brand-blue text-white">{lvl.badge}</span>
                        )}
                      </span>
                      <span className={`block mt-1 text-sm leading-relaxed transition-colors ${on ? "text-slate-600" : "text-slate-500"}`}>{lvl.short}</span>
                    </span>
                    <span className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${on ? "bg-brand-blue scale-110" : "bg-white border-2 border-slate-200 group-hover:border-brand-blue/60"}`}>
                      {on && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                </Reveal>
              );
            })}

            {/* ADD-ON ROW — visually distinct from the main levels */}
            <Reveal delay={0.25}>
              <div
                className={`rounded-2xl border border-dashed px-5 md:px-6 py-4 flex items-center justify-between gap-4 transition-colors duration-300 ${
                  mellan ? "border-success/50 bg-success/5" : "border-slate-200 bg-slate-50/60"
                }`}
                data-testid="addon-row"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${mellan ? "bg-success text-white" : "bg-white border border-slate-200 text-brand-blue"}`}>
                    {mellan ? <Check className="h-4 w-4" strokeWidth={3} /> : <Plus className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-ink">Mellan fönstren <span className="ml-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Tillval</span></p>
                    <p className="text-xs text-slate-500 truncate">För kopplade eller fyrsidiga fönster</p>
                  </div>
                </div>
                {mellan ? (
                  <button type="button" onClick={removeMellan} data-testid="addon-remove" className="text-xs font-semibold text-slate-500 hover:text-brand-ink transition-colors shrink-0">
                    Ta bort
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => (selected ? setShowMellan(true) : toast.info("Välj först en nivå av fönsterputs."))}
                    data-testid="addon-open"
                    className="text-xs font-semibold text-brand-blue hover:text-brand-blueHover transition-colors shrink-0"
                  >
                    Läs mer
                  </button>
                )}
              </div>
            </Reveal>
          </div>

          {/* DETAIL + SUMMARY PANEL */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal delay={0.15}>
              <div className="rounded-3xl bg-brand-ink text-white p-7 md:p-8 relative overflow-hidden" data-testid="configurator-panel">
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-blue/25 blur-3xl" aria-hidden="true" />
                <AnimatePresence mode="wait">
                  {selected ? (
                    <motion.div
                      key={selected.id + (mellan ? "-m" : "")}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="relative"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-ice/70">Din bokning</p>
                      <h3 className="mt-3 font-heading text-3xl font-bold tracking-tight" data-testid="panel-level-name">{selected.name}</h3>
                      <p className="mt-3 text-white/70 leading-relaxed text-sm">{selected.detail}</p>
                      {mellan && (
                        <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-success/20 text-emerald-300 px-3 py-1.5 rounded-md" data-testid="panel-mellan-tag">
                          <Check className="h-3.5 w-3.5" strokeWidth={3} /> + Mellan fönstren
                        </p>
                      )}
                      <button
                        onClick={goBooking}
                        data-testid="booking-summary-cta"
                        className="group mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-ink font-semibold px-6 py-4 hover:bg-brand-ice active:scale-[0.99] transition-all"
                      >
                        Gå vidare till bokning <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                      <p className="mt-3.5 text-center text-xs text-white/50">Kostnadsfri offert – utan förpliktelser</p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="relative"
                      data-testid="panel-empty"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-ice/70">Din bokning</p>
                      <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight">Börja med att välja en nivå</h3>
                      <p className="mt-3 text-white/70 leading-relaxed text-sm">
                        Välj Utsida, In och utsida eller Premium till vänster. Har du kopplade fönster kan du sedan lägga till putsning mellan fönstren.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>

        {/* OTHER SERVICES — slim rows, not cards */}
        <div className="mt-16 md:mt-20">
          <Reveal>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-ink tracking-tight">Mer än bara rena fönster.</h3>
            <p className="mt-2 text-slate-600">Vi hjälper dig att hålla hela utsidan ren, fräsch och välskött.</p>
          </Reveal>
          <div className="mt-6 divide-y divide-slate-100 border-y border-slate-100">
            {OTHER_SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <Link
                  to={`/tjanster/${s.id}`}
                  data-testid={`other-service-${s.id}`}
                  className="group w-full flex items-center justify-between gap-4 py-5 text-left"
                >
                  <span>
                    <span className="font-semibold text-brand-ink group-hover:text-brand-blue transition-colors">{s.label}</span>
                    <span className="block text-sm text-slate-500 mt-0.5">{s.short}</span>
                  </span>
                  <span className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center shrink-0 group-hover:bg-brand-blue group-hover:border-brand-blue transition-all duration-300">
                    <ArrowRight className="h-4 w-4 text-brand-blue group-hover:text-white transition-colors" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <MellanModal open={showMellan} onAdd={addMellan} onDecline={declineMellan} />
      <BookingBar
        services={selected ? ["fonsterputs"] : []}
        fonsterputsLevel={selected?.name}
        mellanFonstren={mellan}
        onRemoveMellan={removeMellan}
        onContinue={goBooking}
        continueLabel="Boka fönsterputs"
      />
    </section>
  );
};

export default Configurator;
